import { useState, useCallback, useEffect } from 'react';
import { ILightboxImage } from '../types/lightbox.types';

interface UseLightboxProps {
  images: ILightboxImage[];
  initialIndex: number;
  onClose: () => void;
}

export const useLightbox = ({ images, initialIndex, onClose }: UseLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Resetear índice cuando cambie el initialIndex
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const goToImage = useCallback(
    (index: number) => {
      if (index >= 0 && index < images.length) {
        setCurrentIndex(index);
      }
    },
    [images.length],
  );

  // Manejo de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext, onClose]);

  const currentImage = images[currentIndex];
  const hasPrevious = images.length > 1;
  const hasNext = images.length > 1;

  return {
    currentIndex,
    currentImage,
    goToPrevious,
    goToNext,
    goToImage,
    hasPrevious,
    hasNext,
  };
};
